/**
 * Client-side reader for the stored auth JWT.
 *
 * The backend exposes only POST /auth/login and POST /auth/refresh-token —
 * there is NO /auth/me. So a persisted session is restored on startup by
 * decoding the token we already hold rather than by asking the backend who we
 * are. The signature is not (and cannot be) verified here: that is the
 * backend's job on every real API call. All this decides is whether to render
 * an authenticated shell on boot, and a forged token buys nothing, since every
 * request behind it is still checked server-side.
 */

export interface AuthTokenClaims {
  sub?: string;
  user_id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  role?: string;
  exp?: number;
}

/** Decode a JWT payload without verifying the signature. */
export function decodeAuthToken(token: string): AuthTokenClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // base64url → base64, then pad to a multiple of 4 for atob.
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const decoded = JSON.parse(atob(padded));
    return decoded && typeof decoded === 'object' ? decoded : null;
  } catch {
    return null;
  }
}

/**
 * True only when the token carries an `exp` that has already passed.
 *
 * A token with no `exp` is treated as NOT expired: the backend is the
 * authority on validity, and guessing "expired" here would log out a user
 * holding a perfectly good credential.
 */
export function isTokenExpired(claims: AuthTokenClaims | null): boolean {
  if (!claims || typeof claims.exp !== 'number') return false;
  return Date.now() / 1000 >= claims.exp;
}
